/**
 * White-label: wstrzykiwanie brandingu konta do DOM (ARCHITECTURE.md § White-label).
 *
 * Branding przychodzi z `GET /api/me` (`MeResponse.branding`) i laduje w CSS
 * custom properties na `<html>`. Komponenty nie czytaja brandingu przez propsy —
 * uzywaja `var(--brand-accent)`, wiec zmiana konta jest jedna operacja na
 * dokumencie, bez przerysowania drzewa.
 */

import { useEffect } from 'react';
import type { Branding } from '@shared/api-types';
import { blobUrl } from './api';

/** Wartosci domyslne musza zgadzac sie z `src/web/index.css`. */
export const DEFAULT_BRAND_ACCENT = 'oklch(0.72 0.15 250)';
export const DEFAULT_BRAND_PRIMARY = 'oklch(0.32 0.02 250)';
export const DEFAULT_PRODUCT_NAME = 'GenAI Studio';

const BRAND_VARS = {
  accent: '--brand-accent',
  primary: '--brand-primary',
  font: '--brand-font',
} as const;

/**
 * Ustawia zmienne CSS i tytul dokumentu. `null` (brak brandingu albo brak
 * sesji) przywraca stan domyslny — inaczej po wylogowaniu zostalyby kolory
 * poprzedniego konta.
 */
export function applyBranding(branding: Branding | null): void {
  const root = document.documentElement;

  setVar(root, BRAND_VARS.accent, branding?.accentHex ?? null, DEFAULT_BRAND_ACCENT);
  setVar(root, BRAND_VARS.primary, branding?.primaryHex ?? null, DEFAULT_BRAND_PRIMARY);
  setVar(root, BRAND_VARS.font, branding?.font ?? null, null);

  const productName = branding?.productName ?? null;
  document.title = productName === null || productName.length === 0 ? DEFAULT_PRODUCT_NAME : productName;

  applyFavicon(branding?.faviconKey ?? null);
}

function setVar(root: HTMLElement, name: string, value: string | null, fallback: string | null): void {
  const next = value !== null && value.length > 0 ? value : fallback;
  if (next === null) {
    root.style.removeProperty(name);
    return;
  }
  root.style.setProperty(name, next);
}

function applyFavicon(faviconKey: string | null): void {
  if (faviconKey === null || faviconKey.length === 0) return;
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const link = existing ?? document.createElement('link');
  link.rel = 'icon';
  link.href = blobUrl(faviconKey);
  if (existing === null) document.head.appendChild(link);
}

/** Logo konta jako URL bloba albo `null`, gdy konto nie ma wlasnego. */
export function brandLogoUrl(branding: Branding | null): string | null {
  const key = branding?.logoKey ?? null;
  return key === null || key.length === 0 ? null : blobUrl(key);
}

export function useBranding(branding: Branding | null | undefined): void {
  useEffect(() => {
    applyBranding(branding ?? null);
  }, [branding]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Motyw ciemny / jasny
// ─────────────────────────────────────────────────────────────────────────────

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

const THEME_STORAGE_KEY = 'gs.theme';

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

export function readStoredTheme(): Theme {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : 'system';
  } catch {
    // Prywatny tryb przegladarki potrafi rzucac na localStorage.
    return 'system';
  }
}

export function storeTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* brak persystencji nie jest bledem UI */
  }
}

export function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Klasa `.dark` na `<html>` — wariant Tailwinda jest podpiety pod nia w index.css. */
export function applyTheme(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
}
